/**
 * LinkChip type registry — styling, tooltips, and navigation for each LinkChipType.
 *
 * HOW TO EXTEND
 * 1. Add a value to the LinkChipType enum (linkChip.types.ts).
 * 2. Add static sx / getSx / getHref / getTooltip helpers here as needed.
 * 3. Register a LinkChipTypeConfig entry in LINK_CHIP_TYPE_CONFIG.
 *
 * CONFIG SHAPE
 * - variant / color / sx: fixed MUI Chip presentation for the type.
 * - getSx: per-chip styles when they depend on id/name (e.g. table type colors).
 * - getHref / getTooltip: optional. Omit for display-only chips.
 *   getHref should return "" when it cannot build a URL (no productLineId, etc.);
 *   LinkChip then renders a non-clickable Chip instead of an anchor.
 *
 * productLineId is supplied by LinkChip from Redux; builders must tolerate "".
 */
import { ChipProps, SxProps, Theme } from "@mui/material";
import { TablesTypeEnum } from "types/table";
import colors from "theme/colors";
import { GetParentPropertyName } from "modules/utility/utility";
import { LinkChipInfo, LinkChipType } from "./linkChip.types";

export type LinkChipTypeConfig = {
  /** MUI Chip variant. */
  readonly variant: "filled" | "outlined";
  /** MUI Chip color token (e.g. "primary"). Prefer sx for custom palettes. */
  readonly color?: ChipProps["color"];
  /** Fixed styles applied to every chip of this type. */
  readonly sx?: SxProps<Theme>;
  /**
   * Styles derived from the chip's id/name. When both sx and getSx are set,
   * LinkChip uses getSx and ignores the static sx.
   */
  readonly getSx?: (chipInfo: LinkChipInfo) => SxProps<Theme>;
  /**
   * Build the navigation URL. Empty string → non-link Chip.
   * Second arg is the selected product line id (may be "").
   */
  readonly getHref?: (chipInfo: LinkChipInfo, productLineId: string) => string;
  /**
   * Hover text. May use productLineId if the copy needs it; most tooltips
   * only need chipInfo.name / id.
   */
  readonly getTooltip?: (
    chipInfo: LinkChipInfo,
    productLineId: string
  ) => string | undefined;
};

// ---------------------------------------------------------------------------
// TABLE_TYPE styles — one palette per TablesTypeEnum value shown on the chip
// ---------------------------------------------------------------------------

type TableTypeChipSx = {
  readonly backgroundColor: string;
  readonly borderColor: string;
  readonly color: string;
};

/**
 * Map of INCLUDE / EXCLUDE / LOOKUP / ATTR_LOOKUP → chip colors.
 * Keys must stay in sync with TablesTypeEnum. Update colors here when design
 * changes; LinkChipType.TABLE_TYPE resolves style via getTableTypeChipSx(name).
 */
const TABLE_TYPE_CHIP_SX: Record<TablesTypeEnum, TableTypeChipSx> = {
  /** Include / positively constrained tables — green. */
  [TablesTypeEnum.INCLUDE]: {
    backgroundColor: "#E8F5E9",
    borderColor: "#2E7D32",
    color: "#1B5E20",
  },
  /** Exclude tables — red. */
  [TablesTypeEnum.EXCLUDE]: {
    backgroundColor: "#FFEBEE",
    borderColor: "#C62828",
    color: "#B71C1C",
  },
  /** Lookup tables — blue. */
  [TablesTypeEnum.LOOKUP]: {
    backgroundColor: colors.background.primaryLabels,
    borderColor: "#027BFD",
    color: "#01579B",
  },
  /** Attribute-lookup tables — purple. */
  [TablesTypeEnum.ATTR_LOOKUP]: {
    backgroundColor: "#F3E5F5",
    borderColor: "#7B1FA2",
    color: "#4A148C",
  },
};

/** Fallback when chip name is empty or not a known TablesTypeEnum. */
const DEFAULT_TABLE_TYPE_CHIP_SX: TableTypeChipSx = {
  backgroundColor: "#F5F5F5",
  borderColor: "#9E9E9E",
  color: "#616161",
};

/**
 * Resolve TABLE_TYPE colors from the display name (chipInfo.name).
 * Normalizes case so "include" / "INCLUDE" both match TablesTypeEnum.
 */
function getTableTypeChipSx(tableType: string): TableTypeChipSx {
  const normalizedType = tableType.trim().toUpperCase();

  if (normalizedType && normalizedType in TABLE_TYPE_CHIP_SX) {
    return TABLE_TYPE_CHIP_SX[normalizedType as TablesTypeEnum];
  }

  return DEFAULT_TABLE_TYPE_CHIP_SX;
}

// ---------------------------------------------------------------------------
// Shared static chip styles
// ---------------------------------------------------------------------------

/** Grey filled chip used for ruleset names (PROPERTY / TABLE screens). */
const RULESET_CHIP_SX = {
  backgroundColor: colors.background.secondaryLabels,
  borderColor: colors.primary,
  color: colors.primary,
} as const;

/**
 * Outlined chip for dependency (general) property links.
 * Light blue fill keeps it distinct from green lookup property chips.
 */
const DEPENDENCY_PROPERTY_CHIP_SX = {
  backgroundColor: colors.background.primaryLabels,
  borderColor: "#1976d2",
  color: colors.primary,
} as const;

/**
 * Green outlined chip for lookup property links.
 * Distinct from DEPENDENCY_PROPERTY_CHIP_SX so lookup vs dependency
 * properties are visually distinguishable in grids.
 */
const LOOKUP_PROPERTY_CHIP_SX = {
  backgroundColor: "#E8F5E9",
  borderColor: "#2E7D32",
  color: colors.primary,
} as const;

/** Red outlined chip for validation error counts. */
const VALIDATION_COUNT_ERROR_CHIP_SX = {
  backgroundColor: "#FFEBEE",
  borderColor: "#C62828",
  color: "#B71C1C",
} as const;

/** Orange outlined chip for validation warning counts. */
const VALIDATION_COUNT_WARNING_CHIP_SX = {
  backgroundColor: colors.background.primaryLabelsOrange,
  borderColor: "#E65100",
  color: "#E65100",
} as const;

/** Blue outlined chip for validation info counts. */
const VALIDATION_COUNT_INFO_CHIP_SX = {
  backgroundColor: colors.background.primaryLabels,
  borderColor: colors.secondary,
  color: "#01579B",
} as const;

// ---------------------------------------------------------------------------
// URL + tooltip builders (used by linking chip types)
// ---------------------------------------------------------------------------

/**
 * Properties page URL for a property chip.
 * Uses GetParentPropertyName so dotted subproperty labels (e.g. "Foo.BAR")
 * still deep-link to the parent property record.
 * Returns "" when productLineId is missing so LinkChip stays non-clickable.
 */
function buildPropertyHref(
  chipInfo: LinkChipInfo,
  productLineId: string
): string {
  if (!productLineId) {
    return "";
  }

  const parentPropertyName = GetParentPropertyName(chipInfo.name);
  return `/products/${productLineId}/properties?name=${parentPropertyName}`;
}

/** Tooltip for property chips — names the parent property that will open. */
function buildPropertyTooltip(chipInfo: LinkChipInfo): string {
  const parentPropertyName = GetParentPropertyName(chipInfo.name);
  return `Open property ${parentPropertyName} in new tab`;
}

/**
 * Rules-tables page URL for a table chip.
 * Navigates by table id (chipInfo.id); label remains chipInfo.name.
 * Returns "" when productLineId is missing.
 */
function buildTableHref(chipInfo: LinkChipInfo, productLineId: string): string {
  if (!productLineId) {
    return "";
  }

  return `/products/${productLineId}/rules-tables?tableId=${encodeURIComponent(
    chipInfo.id
  )}`;
}

// ---------------------------------------------------------------------------
// Registry — required: every LinkChipType must have an entry
// ---------------------------------------------------------------------------

/**
 * Canonical map from LinkChipType → presentation / navigation.
 * LinkChip indexes this with props.chipType; missing keys are a TypeScript error.
 */
export const LINK_CHIP_TYPE_CONFIG: Record<LinkChipType, LinkChipTypeConfig> = {
  /**
   * Table type badge (INCLUDE, EXCLUDE, …).
   * Display-only; color comes from the type string in chipInfo.name.
   */
  [LinkChipType.TABLE_TYPE]: {
    variant: "outlined",
    getSx: (chipInfo) => getTableTypeChipSx(chipInfo.name),
  },

  /**
   * Clickable table identity. Opens rules-tables filtered to this table id.
   * No custom sx yet — relies on default outlined Chip look.
   */
  [LinkChipType.TABLE_LINK]: {
    variant: "outlined",
    getHref: buildTableHref,
    getTooltip: () => "Open table in new tab",
  },

  /**
   * Ruleset name. Display-only grey chip (same treatment across property/table UIs).
   */
  [LinkChipType.RULESET]: {
    variant: "outlined",
    sx: RULESET_CHIP_SX,
  },

  /**
   * Dependency (or general) property link — outlined primary SX + properties URL.
   * Used e.g. for Table Summary "Dependency Properties".
   */
  [LinkChipType.PROPERTY_LINK]: {
    variant: "outlined",
    sx: DEPENDENCY_PROPERTY_CHIP_SX,
    getHref: buildPropertyHref,
    getTooltip: (chipInfo) => buildPropertyTooltip(chipInfo),
  },

  /**
   * Lookup property link — green outlined + same properties URL as PROPERTY_LINK.
   * Used e.g. for Table Summary "Lookup Properties".
   */
  [LinkChipType.PROPERTY_LINK_LOOKUP]: {
    variant: "outlined",
    sx: LOOKUP_PROPERTY_CHIP_SX,
    getHref: buildPropertyHref,
    getTooltip: (chipInfo) => buildPropertyTooltip(chipInfo),
  },

  /** Display-only validation error count — red outlined. */
  [LinkChipType.VALIDATION_COUNT_ERROR]: {
    variant: "outlined",
    sx: VALIDATION_COUNT_ERROR_CHIP_SX,
  },

  /** Display-only validation warning count — orange outlined. */
  [LinkChipType.VALIDATION_COUNT_WARNING]: {
    variant: "outlined",
    sx: VALIDATION_COUNT_WARNING_CHIP_SX,
  },

  /** Display-only validation info count — blue outlined. */
  [LinkChipType.VALIDATION_COUNT_INFO]: {
    variant: "outlined",
    sx: VALIDATION_COUNT_INFO_CHIP_SX,
  },
};

/**
 * MUI Chip presentation for a LinkChipType — use when rendering a Chip outside
 * of <LinkChip /> (e.g. SearchableDropdown selected-value chips) so colors stay
 * in sync with Table Summary / LinkChip.
 */
export function getLinkChipMuiProps(
  chipType: LinkChipType,
  chipInfo: LinkChipInfo
): Pick<ChipProps, "variant" | "color" | "sx"> {
  const config = LINK_CHIP_TYPE_CONFIG[chipType];
  return {
    variant: config.variant,
    color: config.color,
    sx: config.getSx?.(chipInfo) ?? config.sx,
  };
}
